import { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useCRUD } from "../../api/crud";
import { JOB_ISSUES_ENDPOINT } from "../../consts";
import { SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import CustomCircularProgress from "../ui/customCircularProgress/customCircularProgress.component";

const HEX_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

export interface JobIssueFormValues {
  title: string;
  description: string;
  color: string;
  code: string;
}

const defaultValues: JobIssueFormValues = {
  title: "",
  description: "",
  color: "#1976d2",
  code: "",
};

interface JobIssueFormProps {
  id?: string;
  /** Called with the saved record, so a picker can select one just added. */
  onSuccess?: (data?: any) => void;
}

export default function JobIssueForm({ id, onSuccess }: JobIssueFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { get, create, update, loading } = useCRUD(JOB_ISSUES_ENDPOINT);
  const [formData, setFormData] = useState<JobIssueFormValues>(defaultValues);
  const [loadingInitial, setLoadingInitial] = useState(!!id);

  useEffect(() => {
    if (!id) {
      setLoadingInitial(false);
      return;
    }
    let cancelled = false;

    const load = async () => {
      const response = await get(id);
      if (cancelled) return;
      if (response?.data) {
        const d = response.data;
        setFormData({
          title: d.title ?? "",
          description: d.description ?? "",
          color: d.color && HEX_REGEX.test(d.color) ? d.color : "",
          code: d.code ?? "",
        });
      }
      setLoadingInitial(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id, get]);

  const handleChange = (field: keyof JobIssueFormValues) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }
  ) => {
    const value = e.target.value as string;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    const payload = {
      title: formData.title,
      description: formData.description || undefined,
      color: formData.color && HEX_REGEX.test(formData.color) ? formData.color : null,
      code: formData.code || undefined,
    };

    if (id) {
      const result = await update({ id, ...payload });
      if (result?.success) {
        enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
        onSuccess?.(result.data);
        window.dispatchEvent(new Event("jobIssueUpdated"));
      } else {
        const msg =
          result?.errorData?.message ||
          t("There was an error, please review the form.");
        enqueueSnackbar(msg, ERROR_SNACKBAR);
      }
    } else {
      const result = await create(payload);
      if (result?.data?.id) {
        enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
        onSuccess?.(result.data);
        window.dispatchEvent(new Event("jobIssueAdded"));
      } else {
        const err = result?.errorData;
        const msg =
          (err && typeof err === "object" && "message" in err && (err as { message?: string }).message) ||
          (err && typeof err === "string" ? err : undefined) ||
          t("There was an error, please review the form.");
        enqueueSnackbar(msg, ERROR_SNACKBAR);
      }
    }
  };

  if (loadingInitial) {
    return <CustomCircularProgress />;
  }

  const fieldSx = { flex: { xs: "1 1 100%", sm: 1 }, minWidth: 120 };
  const rowSx = {
    display: "flex",
    gap: 2,
    flexWrap: { xs: "wrap", sm: "nowrap" },
    alignItems: "stretch",
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={rowSx}>
        <TextField
          size="small"
          label={t("Title")}
          value={formData.title}
          onChange={handleChange("title")}
          required
          sx={fieldSx}
        />
        <TextField
          size="small"
          label={t("Code")}
          value={formData.code}
          onChange={handleChange("code")}
          placeholder="C0211"
          inputProps={{ maxLength: 20 }}
          sx={{ width: { xs: "100%", sm: 120 } }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexShrink: 0,
            height: 40,
            borderRadius: 1,
            px: 0.75,
            boxSizing: "border-box",
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "flex-start", sm: "center" },
          }}
        >
          <input
            type="color"
            value={formData.color && HEX_REGEX.test(formData.color) ? formData.color : "#1976d2"}
            onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
            style={{
              width: 36,
              height: 36,
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              padding: 0,
            }}
          />
          <TextField
            size="small"
            value={formData.color}
            onChange={handleChange("color")}
            placeholder="#RRGGBB"
            inputProps={{ maxLength: 7 }}
            sx={{
              width: 86,
              "& .MuiOutlinedInput-root": { height: 32 },
              "& .MuiInputBase-input": { py: 0.5, px: 1, fontSize: "0.8rem" },
            }}
            variant="outlined"
            hiddenLabel
          />
        </Box>
      </Box>

      <TextField
        size="small"
        label={t("Description")}
        value={formData.description}
        onChange={handleChange("description")}
        fullWidth
        multiline
        rows={3}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "stretch", sm: "flex-end" },
          width: "100%",
        }}
      >
        <Button
          variant="contained"
          color="success"
          disabled={loading || !formData.title}
          onClick={handleSubmit}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          {t("Submit")}
        </Button>
      </Box>
    </Box>
  );
}
